import '../../Common.scss';
import './PlayerInformationTab.scss';

import { Button, Checkbox, Select } from '@trackman/web-shared-components';
import { Trans, useTranslation } from 'react-i18next';

import ErrorInfo from 'components/ErrorInfo/ErrorInfo';
import { Input } from '@trackman/web-shared-components';
import { ReactComponent as MapMarkerIcon } from 'images/icons/MapMarker.svg';
import { ReactComponent as ProfileIcon } from 'images/icons/Profile.svg';
import Separator from 'components/Separator/Separator';
import { useGetProfile } from 'queryHooks/Profile/useGetProfile';
import { trimTooManySpaces } from '../../../../utilities/inputValidation';
import { ProfileData, SignUpLists } from 'types/profile';
import { usePlayerInformationFormStore } from 'stores/Profile/ProfileStore';
import { useGetSignUpLists } from 'queryHooks/Profile/useGetSignUpLists';
import { useSaveProfile } from 'queryHooks/Profile/useSaveProfile';

export const PlayerInformationTab = () => {
  const { data: profileData, isPending: isProfileDataPending } = useGetProfile();
  const { data: signUpLists, isPending: isSignUpListsPending } = useGetSignUpLists();

  return (
    <>
      {!isProfileDataPending && !isSignUpListsPending && profileData && signUpLists && (
        <PlayerInformationForm profileData={profileData} signUpLists={signUpLists} />
      )}
    </>
  );
};

const PlayerInformationForm = ({ profileData, signUpLists }: { profileData: ProfileData; signUpLists: SignUpLists }) => {
  const { t } = useTranslation();

  const initFromProfileData = usePlayerInformationFormStore((s) => s.initFromProfileData);
  const profileDataFromStore = usePlayerInformationFormStore((s) => s);
  if (profileData && !profileDataFromStore.isInitialized) {
    initFromProfileData(profileData);
  }

  const setFirstName = usePlayerInformationFormStore((s) => s.setFirstName);
  const setLastName = usePlayerInformationFormStore((s) => s.setLastName);
  const setGender = usePlayerInformationFormStore((s) => s.setGender);
  const setPhone = usePlayerInformationFormStore((s) => s.setPhone);
  const setStreet1 = usePlayerInformationFormStore((s) => s.setStreet1);
  const setStreet2 = usePlayerInformationFormStore((s) => s.setStreet2);
  const setPostalCode = usePlayerInformationFormStore((s) => s.setPostalCode);
  const setCity = usePlayerInformationFormStore((s) => s.setCity);
  const setCountry = usePlayerInformationFormStore((s) => s.setCountry);
  const setState = usePlayerInformationFormStore((s) => s.setState);
  const setAcceptedEmails = usePlayerInformationFormStore((s) => s.setAcceptedEmails);

  const adjustStringValue = (value: string) => {
    const adjustedValue = trimTooManySpaces(value);
    return !adjustedValue.replace(/\s/g, '').length ? '' : adjustedValue;
  };

  const isFormValid = !!profileDataFromStore.firstName && !!profileDataFromStore.lastName && !!profileDataFromStore.address.country;

  const saveProfilePayload: ProfileData = {
    ...profileDataFromStore!,
    firstName: profileDataFromStore.firstName,
    lastName: profileDataFromStore.lastName,
    gender: profileDataFromStore.gender,
    phone: profileDataFromStore.phone,
    address: {
      street1: profileDataFromStore.address.street1,
      street2: profileDataFromStore.address.street2,
      postalCode: profileDataFromStore.address.postalCode,
      city: profileDataFromStore.address.city,
      country: profileDataFromStore.address.country,
      state: profileDataFromStore.address.state,
    },
    acceptedEmails: profileDataFromStore.acceptedEmails,
  };

  const saveProfile = useSaveProfile(saveProfilePayload);

  return (
    <>
      {profileDataFromStore && (
        <>
          <div className='profile-section personal-info-section'>
            <div className='profile-inputs-column'>
              <div className='profile-section-title'>
                <ProfileIcon />
                {t('PersonalInformation', { ns: 'editProfile' })}
              </div>
              <Input
                label={t('PlayerName', { ns: 'editProfile' })}
                value={profileDataFromStore.firstName}
                required
                error={!profileDataFromStore.firstName}
                hint={!profileDataFromStore.firstName ? <ErrorInfo /> : ' '}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(adjustStringValue(e.currentTarget.value))}
              />
              <Input
                label={t('PlayerSurname', { ns: 'editProfile' })}
                value={profileDataFromStore.lastName}
                required
                error={!profileDataFromStore.lastName}
                hint={!profileDataFromStore.lastName ? <ErrorInfo /> : ' '}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(adjustStringValue(e.currentTarget.value))}
              />
              <Select
                label={t('Gender', { ns: 'editProfile' })}
                value={profileDataFromStore.gender}
                hint=' '
                onChange={(event) => setGender(event.target.value as string)}
                selectOptions={[
                  { label: t('Male', { ns: 'editProfile' }), value: 'male' },
                  { label: t('Female', { ns: 'editProfile' }), value: 'female' },
                ]}
              />
              <Input
                label={t('MobilePhone', { ns: 'editProfile' })}
                value={profileDataFromStore.phone}
                hint=' '
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(adjustStringValue(e.currentTarget.value))}
              />
              <div className='newsletter-checkbox'>
                <Checkbox
                  onChange={() => setAcceptedEmails(!profileDataFromStore.acceptedEmails)}
                  checked={profileDataFromStore.acceptedEmails}
                  label={
                    <p>
                      <Trans i18nKey='ReceiveOffersAndPromotionsFromTrackman' ns='editProfile'>
                        Receive offers and promotions from Trackman&nbsp;
                        <span>(CVR.DK27352677)</span>
                      </Trans>
                    </p>
                  }
                />
              </div>
            </div>
            <div className='profile-inputs-column'>
              <div className='profile-section-title'>
                <MapMarkerIcon />
                {t('AddressInformation', { ns: 'editProfile' })}
              </div>
              <Input
                label={t('StreetName1', { ns: 'editProfile' })}
                value={profileDataFromStore.address.street1}
                hint=' '
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStreet1(adjustStringValue(e.currentTarget.value))}
              />
              <Input
                label={t('StreetName2', { ns: 'editProfile' })}
                value={profileDataFromStore.address.street2}
                hint=' '
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStreet2(adjustStringValue(e.currentTarget.value))}
              />
              <Input
                label={t('PostalCode', { ns: 'editProfile' })}
                value={profileDataFromStore.address.postalCode}
                hint=' '
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostalCode(adjustStringValue(e.currentTarget.value))}
              />
              <Input
                label={t('City', { ns: 'editProfile' })}
                value={profileDataFromStore.address.city}
                hint=' '
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(adjustStringValue(e.currentTarget.value))}
              />
              <Select
                label={t('Country', { ns: 'editProfile' })}
                value={profileDataFromStore.address.country}
                hint=' '
                required={true}
                error={!profileDataFromStore.address.country}
                onChange={(event) => setCountry(event.target.value as string)}
                selectOptions={signUpLists?.Countries.map((country) => {
                  return {
                    label: country.name === ' ' ? t('PleaseSelectACountry', { ns: 'editProfile' }) : country.name,
                    value: country.name,
                  };
                })}
              />
              {profileDataFromStore.address.country === 'United States' && (
                <Select
                  label={t('State', { ns: 'editProfile' })}
                  value={profileDataFromStore.address.state}
                  hint=' '
                  onChange={(event) => setState(event.target.value as string)}
                  selectOptions={signUpLists?.States.map((state) => {
                    return {
                      label: state === ' ' ? t('PleaseSelectAState', { ns: 'editProfile' }) : state,
                      value: state,
                    };
                  })}
                />
              )}
              <div className='button-wrapper'>
                <Button
                  label={t('SaveChanges', { ns: 'editProfile' })}
                  onClick={() => saveProfile.mutate()}
                  intent='primary'
                  isLoading={saveProfile.isPending}
                  disabled={!isFormValid || saveProfile.isPending}
                  className='styled-button primary'
                />
                {!isFormValid ? <ErrorInfo errorMsg={t('ProfileInformationIsMissing', { ns: 'editProfile' })} position='initial' /> : null}
              </div>
            </div>
          </div>
          <Separator />
        </>
      )}
    </>
  );
};

export default PlayerInformationTab;
